import { useCallback, useEffect, useRef } from 'react';
import { logSessionInfo } from '../../services/logger';

/**
 * A tile's appearance is inferred from which properties the option carries; there is no
 * option-type field. An option with audioText but no text is spoken and drawn as an icon,
 * because Formative has no recorded audio to attach.
 */
export const optionKinds = { video: 'video', image: 'image', audio: 'audio', text: 'text' };

export const optionKind = (option) => {
  if (option?.videoUrl) return optionKinds.video;
  if (option?.imageUrl) return optionKinds.image;
  if (option?.audioText && !option?.text) return optionKinds.audio;
  return optionKinds.text;
};

/** Plays through a media element, so it needs the one-at-a-time controller below. */
export const optionHasMedia = (option) => !!option?.videoUrl;

/** Carries its own words to speak, rather than relying on readOptionsAloud. */
export const optionIsSpoken = (option) => !!option?.audioText;

/** Makes a sound of its own when its turn comes, whichever way it does it. */
export const optionNarratesItself = (option) =>
  optionHasMedia(option) || optionIsSpoken(option);

/** Tiles that are not a plain text label or a picture get the media layout. */
export const optionsNeedMediaLayout = (options) =>
  (options || []).some((option) => {
    const kind = optionKind(option);
    return kind === optionKinds.video || kind === optionKinds.audio;
  });

/**
 * One option's media plays at a time. Starting a new one cuts the current one short and
 * resolves its pending promise, so an awaiting reveal loop advances instead of hanging.
 */
export default function useOptionMediaPlayback(speech) {
  const videoElsRef = useRef(new Map());
  const activeRef = useRef(null);
  const passTokenRef = useRef(null);
  const speechRef = useRef(speech);
  speechRef.current = speech;

  const releaseAudioForPlayback = useCallback(() => {
    const speechApi = speechRef.current;
    speechApi?.prepareMicForPlayback?.();
    speechApi?.cancelRecorderStreamIfNeeded?.();
    speechApi?.abortSpeech?.();
  }, []);

  const restoreAudioAfterPlayback = useCallback(() => {
    const speechApi = speechRef.current;
    speechApi?.prepareMicForRecord?.();
    speechApi?.restartRecorderStreamIfNeeded?.();
  }, []);

  const beginPass = useCallback((token) => {
    if (passTokenRef.current === token) return;
    const alreadyOpen = passTokenRef.current !== null;
    passTokenRef.current = token;
    if (!alreadyOpen) releaseAudioForPlayback();
  }, [releaseAudioForPlayback]);

  const endPass = useCallback((token) => {
    if (passTokenRef.current === null || passTokenRef.current !== token) return;
    passTokenRef.current = null;
    restoreAudioAfterPlayback();
  }, [restoreAudioAfterPlayback]);

  const stop = useCallback((restoreAudio = true) => {
    const active = activeRef.current;
    if (!active) return;
    activeRef.current = null;
    const { el, resolve } = active;
    el.onended = null;
    el.onerror = null;
    try {
      el.pause();
      el.currentTime = 0;
    } catch (err) {
      // element may not be seekable yet
    }
    if (restoreAudio && passTokenRef.current === null) restoreAudioAfterPlayback();
    resolve();
  }, [restoreAudioAfterPlayback]);

  const registerVideoElement = useCallback((key, el) => {
    if (el) {
      videoElsRef.current.set(key, el);
    } else {
      videoElsRef.current.delete(key);
    }
  }, []);

  const play = useCallback(
    (option) => {
      if (!optionHasMedia(option)) return Promise.resolve();

      const el = videoElsRef.current.get(option.key);
      if (!el) return Promise.resolve();

      stop(false);
      if (passTokenRef.current === null) releaseAudioForPlayback();

      return new Promise((resolve) => {
        const finish = () => {
          if (activeRef.current?.el !== el) return;
          activeRef.current = null;
          el.onended = null;
          el.onerror = null;
          if (passTokenRef.current === null) restoreAudioAfterPlayback();
          resolve();
        };
        activeRef.current = { key: option.key, el, resolve };
        el.onended = finish;
        el.onerror = finish;
        try {
          el.currentTime = 0;
        } catch (err) {
          // element may not be seekable yet
        }
        el.volume = 1.0;
        const started = el.play();
        if (started?.catch) {
          started.catch((err) => {
            if (activeRef.current?.el === el) {
              logSessionInfo('mmc_option_video_play_error', {
                videoUrl: option.videoUrl,
                error: err?.message,
                name: err?.name,
              });
            }
            finish();
          });
        }
      });
    },
    [releaseAudioForPlayback, restoreAudioAfterPlayback, stop],
  );

  useEffect(() => {
    const videoEls = videoElsRef.current;
    return () => {
      const active = activeRef.current;
      if (passTokenRef.current !== null || active) {
        passTokenRef.current = null;
        restoreAudioAfterPlayback();
      }
      if (active) {
        activeRef.current = null;
        try {
          active.el.pause();
        } catch (err) {
          // element already torn down
        }
        active.resolve();
      }
      videoEls.clear();
    };
  }, [restoreAudioAfterPlayback]);

  return { play, stop, beginPass, endPass, registerVideoElement };
}
